import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { axiosInstance } from "../utils/axios";

const DriverContext = createContext();

export const DriverProvider = ({ children }) => {
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [driverDetails, setDriverDetails] = useState(null);
  const [driverStats, setDriverStats] = useState(null);
  // The tables under the simulator dropdown. Separate from `driverStats` because the KPI tiles
  // above them are pinned to the pooled rollup and must not react to the dropdown.
  const [driverTableStats, setDriverTableStats] = useState(null);
  const [eloHistory, setEloHistory] = useState([]);
  const [resultsHistory, setResultsHistory] = useState([]);
  const [recentEvents, setRecentEvents] = useState([]);
  const [isDetailsLoading, setIsDetailsLoading] = useState(false);
  const [isStatsLoading, setIsStatsLoading] = useState(false);
  const [isTableStatsLoading, setIsTableStatsLoading] = useState(false);
  const [isEloHistoryLoading, setIsEloHistoryLoading] = useState(false);
  const [isResultsHistoryLoading, setIsResultsHistoryLoading] = useState(false);
  const [isEventsLoading, setIsEventsLoading] = useState(false);
  // null = the pooled rollup across every simulator (the default). Set once Phase 10 added a
  // `simulator` query param to /drivers/{id}/stats; without a control on this page too, this page
  // and the Hall of Fame board it was opened from could disagree about a driver's win count with
  // nothing on screen explaining why.
  const [selectedSimulatorId, setSelectedSimulatorId] = useState(null);

  const fetchDriverDetails = useCallback(async (driverId) => {
    try {
      setIsDetailsLoading(true);
      const response = await axiosInstance.get(`/drivers/${driverId}`);
      setDriverDetails(response.data);
    } catch (error) {
      console.error("Error fetching driver details:", error);
      // Set driver details to null in case of error
      setDriverDetails(null);
    } finally {
      setIsDetailsLoading(false);
    }
  }, []);

  // The pooled rollup across every simulator — feeds the KPI tiles, which stay simulator-agnostic.
  const fetchDriverStats = useCallback(async (driverId) => {
    try {
      setIsStatsLoading(true);
      const response = await axiosInstance.get(`/drivers/${driverId}/stats`);
      setDriverStats(response.data._embedded?.stats ?? []);
    } catch (error) {
      console.error("Error fetching driver stats:", error);
      setDriverStats(null);
    } finally {
      setIsStatsLoading(false);
    }
  }, []);

  // The simulator-scoped figures behind the detail tables.
  const fetchDriverTableStats = useCallback(async (driverId, simulatorId) => {
    try {
      setIsTableStatsLoading(true);
      const response = await axiosInstance.get(`/drivers/${driverId}/stats`, {
        params: { simulator: simulatorId },
      });
      setDriverTableStats(response.data._embedded?.stats ?? []);
    } catch (error) {
      console.error("Error fetching driver table stats:", error);
      setDriverTableStats(null);
    } finally {
      setIsTableStatsLoading(false);
    }
  }, []);

  const fetchEloHistory = useCallback(async (driverId) => {
    try {
      setIsEloHistoryLoading(true);
      const response = await axiosInstance.get(`/drivers/${driverId}/elo-history`);
      setEloHistory(response.data._embedded?.eloPoints || []);
    } catch (error) {
      console.error("Error fetching driver elo history:", error);
      setEloHistory([]);
    } finally {
      setIsEloHistoryLoading(false);
    }
  }, []);

  const fetchResultsHistory = useCallback(async (driverId) => {
    try {
      setIsResultsHistoryLoading(true);
      const response = await axiosInstance.get(`/drivers/${driverId}/results-history`);
      setResultsHistory(response.data._embedded?.resultPoints || []);
    } catch (error) {
      console.error("Error fetching driver results history:", error);
      setResultsHistory([]);
    } finally {
      setIsResultsHistoryLoading(false);
    }
  }, []);

  const fetchRecentEvents = useCallback(async (driverId) => {
    try {
      setIsEventsLoading(true);
      const response = await axiosInstance.get(`/drivers/${driverId}/recent-events`);
      setRecentEvents(response.data._embedded?.driverResults || []);
    } catch (error) {
      console.error("Error fetching recent events:", error);
      setRecentEvents([]);
    } finally {
      setIsEventsLoading(false);
    }
  }, []);

  const resetDriverContext = useCallback(() => {
    setSelectedDriver(null);
    setDriverDetails(null);
    setDriverStats(null);
    setDriverTableStats(null);
    setRecentEvents([]);
    setEloHistory([]);
    setResultsHistory([]);
    setIsDetailsLoading(false);
    setIsStatsLoading(false);
    setIsTableStatsLoading(false);
    setIsEventsLoading(false);
    setIsEloHistoryLoading(false);
    setIsResultsHistoryLoading(false);
    setSelectedSimulatorId(null);
  }, []);

  useEffect(() => {
    if (selectedDriver) {
      fetchDriverDetails(selectedDriver.id);
      fetchRecentEvents(selectedDriver.id);
      fetchDriverStats(selectedDriver.id);
      // Both charts span every simulator, so they are fetched once per driver and never refetched
      // when the simulator dropdown changes.
      fetchEloHistory(selectedDriver.id);
      fetchResultsHistory(selectedDriver.id);
    } else {
      setDriverDetails(null);
      setRecentEvents([]);
      setDriverStats(null);
      setEloHistory([]);
      setResultsHistory([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedDriver,
    fetchDriverDetails,
    fetchRecentEvents,
    fetchDriverStats,
    fetchEloHistory,
    fetchResultsHistory,
  ]);

  useEffect(() => {
    // With no simulator chosen the tables show the same pooled rollup as the tiles, so reuse it
    // rather than firing an identical second request.
    if (selectedDriver && selectedSimulatorId) {
      fetchDriverTableStats(selectedDriver.id, selectedSimulatorId);
    } else {
      setDriverTableStats(null);
    }
  }, [selectedDriver, selectedSimulatorId, fetchDriverTableStats]);

  return (
    <DriverContext.Provider
      value={{
        selectedDriver,
        setSelectedDriver,
        driverDetails,
        driverStats,
        // Falls back to the pooled rollup whenever no simulator is selected.
        driverTableStats: driverTableStats ?? driverStats,
        recentEvents,
        eloHistory,
        resultsHistory,
        isDetailsLoading,
        isStatsLoading,
        isTableStatsLoading,
        isEventsLoading,
        isEloHistoryLoading,
        isResultsHistoryLoading,
        selectedSimulatorId,
        setSelectedSimulatorId,
        resetDriverContext,
        fetchDriverDetails,
        fetchDriverStats,
        fetchDriverTableStats,
        fetchRecentEvents,
        fetchEloHistory,
        fetchResultsHistory,
      }}
    >
      {children}
    </DriverContext.Provider>
  );
};

export const useDriverContext = () => {
  return useContext(DriverContext);
};
